import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Filter,
  Globe2,
  MonitorSmartphone,
  RefreshCw,
  Search,
  Server,
  Shield,
  UserRound,
  Users,
  Wifi,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  ApiLogsResponse,
  ApiRequestLog,
  getApiLogs,
} from "../logger/apiLogsApi";

const requesterOptions = [
  { label: "All Requesters", value: "" },
  { label: "Customers", value: "customer_portal" },
  { label: "Technicians", value: "technician" },
  { label: "Resellers", value: "reseller_portal" },
];

const methodOptions = [
  { label: "All Methods", value: "" },
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
  { label: "PUT", value: "PUT" },
  { label: "PATCH", value: "PATCH" },
  { label: "DELETE", value: "DELETE" },
];

const statusOptions = [
  { label: "All Status", value: "" },
  { label: "200 OK", value: "200" },
  { label: "201 Created", value: "201" },
  { label: "400 Bad Request", value: "400" },
  { label: "401 Unauthorized", value: "401" },
  { label: "403 Forbidden", value: "403" },
  { label: "404 Not Found", value: "404" },
  { label: "500 Server Error", value: "500" },
];

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatNumber(value?: number | null) {
  return new Intl.NumberFormat("en-KE").format(Number(value || 0));
}

function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-KE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Africa/Nairobi",
  }).format(date);
}

function formatDuration(value?: number | null) {
  const duration = Number(value || 0);

  if (duration >= 1000) {
    return `${(duration / 1000).toFixed(2)}s`;
  }

  return `${duration.toFixed(2)}ms`;
}

function getRequesterLabel(type?: string | null) {
  if (!type) return "Unknown";

  const map: Record<string, string> = {
    customer_portal: "Customer",
    technician: "Technician",
    reseller_portal: "Reseller",
  };

  return map[type] || type;
}

function getRequesterTone(type?: string | null) {
  switch (type) {
    case "customer_portal":
      return "bg-blue-50 text-blue-700 border-blue-100";
    case "technician":
      return "bg-violet-50 text-violet-700 border-violet-100";
    case "reseller_portal":
      return "bg-amber-50 text-amber-700 border-amber-100";
    default:
      return "bg-slate-50 text-slate-600 border-slate-100";
  }
}

function getMethodClass(method?: string | null) {
  switch ((method || "").toUpperCase()) {
    case "GET":
      return "bg-sky-50 text-sky-700 border-sky-100";
    case "POST":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "PUT":
    case "PATCH":
      return "bg-amber-50 text-amber-700 border-amber-100";
    case "DELETE":
      return "bg-rose-50 text-rose-700 border-rose-100";
    default:
      return "bg-slate-50 text-slate-700 border-slate-100";
  }
}

function getStatusMeta(statusCode?: number | null) {
  const code = Number(statusCode || 0);

  if (code >= 500) {
    return {
      label: "Server Error",
      icon: XCircle,
      className: "bg-rose-50 text-rose-700 border-rose-100",
      dot: "bg-rose-500",
    };
  }

  if (code >= 400) {
    return {
      label: "Client Error",
      icon: AlertTriangle,
      className: "bg-orange-50 text-orange-700 border-orange-100",
      dot: "bg-orange-500",
    };
  }

  if (code >= 300) {
    return {
      label: "Redirect",
      icon: RefreshCw,
      className: "bg-blue-50 text-blue-700 border-blue-100",
      dot: "bg-blue-500",
    };
  }

  if (code >= 200) {
    return {
      label: "Success",
      icon: CheckCircle2,
      className: "bg-emerald-50 text-emerald-700 border-emerald-100",
      dot: "bg-emerald-500",
    };
  }

  return {
    label: "Unknown",
    icon: AlertTriangle,
    className: "bg-slate-50 text-slate-700 border-slate-100",
    dot: "bg-slate-400",
  };
}

function StatCard({
  title,
  value,
  icon,
  helper,
  className,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  helper: string;
  className: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/80 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg md:p-5">
      <div
        className={cn(
          "absolute right-[-32px] top-[-32px] h-24 w-24 rounded-full opacity-15 blur-2xl",
          className,
        )}
      />

      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>

          <h3 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
            {formatNumber(value)}
          </h3>

          <p className="mt-1 text-xs text-slate-400">{helper}</p>
        </div>

        <div
          className={cn(
            "rounded-2xl p-3 text-white shadow-lg transition group-hover:scale-105",
            className,
          )}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function MiniMetric({
  title,
  value,
  icon,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-white/15 p-2 text-white">{icon}</div>

        <div>
          <p className="text-xs text-white/60">{title}</p>
          <p className="text-xl font-black text-white">{formatNumber(value)}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ statusCode }: { statusCode: number }) {
  const meta = getStatusMeta(statusCode);
  const Icon = meta.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold",
        meta.className,
      )}
    >
      <Icon size={13} />
      {statusCode}
    </span>
  );
}

function MethodBadge({ method }: { method: string }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full border px-2.5 py-1 text-xs font-black",
        getMethodClass(method),
      )}
    >
      {method}
    </span>
  );
}

function RequesterBadge({ type }: { type?: string | null }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full border px-2.5 py-1 text-xs font-bold",
        getRequesterTone(type),
      )}
    >
      {getRequesterLabel(type)}
    </span>
  );
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>

      <p className="mt-1 break-words text-sm font-semibold text-slate-800">
        {value || "—"}
      </p>
    </div>
  );
}

function ExpandedLogDetails({ log }: { log: ApiRequestLog }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <div className="rounded-xl bg-blue-50 p-2 text-blue-700">
            <UserRound size={17} />
          </div>

          <h4 className="font-black text-slate-900">Requester</h4>
        </div>

        <div className="grid gap-3">
          <DetailItem label="Requester ID" value={log.requester_id} />
          <DetailItem label="Requester Type" value={log.requester_type} />
          <DetailItem label="Client ID" value={log.client_id} />
          <DetailItem label="Customer ID" value={log.customer_id} />
          <DetailItem
            label="Reseller Partner ID"
            value={log.reseller_partner_id}
          />
        </div>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <div className="rounded-xl bg-violet-50 p-2 text-violet-700">
            <MonitorSmartphone size={17} />
          </div>

          <h4 className="font-black text-slate-900">Device</h4>
        </div>

        <div className="grid gap-3">
          <DetailItem label="IP Address" value={log.ip_address} />
          <DetailItem label="Device ID" value={log.device_id} />
          <DetailItem label="Device Type" value={log.device_type} />
          <DetailItem label="Operating System" value={log.device_os} />
          <DetailItem label="Browser" value={log.browser} />
          <DetailItem label="Referer" value={log.referer} />
        </div>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <div className="rounded-xl bg-rose-50 p-2 text-rose-700">
            <AlertTriangle size={17} />
          </div>

          <h4 className="font-black text-slate-900">Error / Agent</h4>
        </div>

        <div className="grid gap-3">
          <DetailItem label="Auth Error" value={log.auth_error} />
          <DetailItem label="Error Message" value={log.error_message} />
          <DetailItem label="User Agent" value={log.user_agent} />
        </div>
      </div>
    </div>
  );
}

function DesktopLogRow({ log }: { log: ApiRequestLog }) {
  const [open, setOpen] = useState(false);
  const statusMeta = getStatusMeta(log.status_code);

  return (
    <>
      <tr className="border-b border-slate-100 transition hover:bg-slate-50/80">
        <td className="px-5 py-4 align-top">
          <div className="flex flex-col gap-2">
            <MethodBadge method={log.method} />

            <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400">
              <Clock size={13} />
              {formatDate(log.created_at)}
            </span>
          </div>
        </td>

        <td className="px-5 py-4 align-top">
          <button
            onClick={() => setOpen((prev) => !prev)}
            className="group w-full text-left"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="max-w-[620px] truncate text-sm font-black text-slate-900 group-hover:text-blue-700">
                  {log.path}
                </p>

                {log.query_params ? (
                  <p className="mt-1 max-w-[620px] truncate text-xs font-medium text-slate-400">
                    ?{log.query_params}
                  </p>
                ) : (
                  <p className="mt-1 text-xs font-medium text-slate-400">
                    No query parameters
                  </p>
                )}
              </div>

              <span className="rounded-full border border-slate-200 bg-white p-1 text-slate-400">
                {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </span>
            </div>
          </button>
        </td>

        <td className="px-5 py-4 align-top">
          <div className="flex flex-col gap-2">
            <StatusBadge statusCode={log.status_code} />

            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400">
              <span className={cn("h-2 w-2 rounded-full", statusMeta.dot)} />
              {statusMeta.label}
            </span>
          </div>
        </td>

        <td className="px-5 py-4 align-top">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
              <UserRound size={17} />
            </div>

            <div className="min-w-0">
              <p className="max-w-[240px] truncate text-sm font-black text-slate-900">
                {log.requester_name || getRequesterLabel(log.requester_type)}
              </p>

              <div className="mt-1">
                <RequesterBadge type={log.requester_type} />
              </div>

              <p className="mt-1 max-w-[240px] truncate text-xs text-slate-400">
                {log.requester_phone || log.requester_email || "No contact"}
              </p>
            </div>
          </div>
        </td>

        <td className="px-5 py-4 align-top">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700">
              <Wifi size={13} />
              {log.ip_address || "—"}
            </div>

            <p className="max-w-[260px] truncate text-xs font-medium text-slate-400">
              {[log.device_type, log.device_os, log.browser]
                .filter(Boolean)
                .join(" • ") || "Unknown device"}
            </p>
          </div>
        </td>

        <td className="px-5 py-4 align-top">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-3 py-1.5 text-xs font-black text-white">
            <Zap size={13} />
            {formatDuration(log.duration_ms)}
          </div>
        </td>
      </tr>

      {open && (
        <tr className="border-b border-slate-100 bg-slate-50/70">
          <td colSpan={6} className="px-5 py-5">
            <ExpandedLogDetails log={log} />
          </td>
        </tr>
      )}
    </>
  );
}

function MobileLogCard({ log }: { log: ApiRequestLog }) {
  const [open, setOpen] = useState(false);
  const statusMeta = getStatusMeta(log.status_code);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <MethodBadge method={log.method} />
          <StatusBadge statusCode={log.status_code} />
        </div>

        <button
          onClick={() => setOpen((prev) => !prev)}
          className="rounded-full border border-slate-200 p-2 text-slate-500"
        >
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      <div className="mt-4">
        <p className="break-words text-sm font-black text-slate-950">
          {log.path}
        </p>

        {log.query_params && (
          <p className="mt-1 break-words text-xs font-medium text-slate-400">
            ?{log.query_params}
          </p>
        )}
      </div>

      <div className="mt-4 grid gap-3 rounded-2xl bg-slate-50 p-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold text-slate-400">Requester</span>
          <span className="text-right text-xs font-black text-slate-800">
            {log.requester_name || getRequesterLabel(log.requester_type)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold text-slate-400">Type</span>
          <RequesterBadge type={log.requester_type} />
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold text-slate-400">IP</span>
          <span className="text-right text-xs font-black text-slate-800">
            {log.ip_address || "—"}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold text-slate-400">Duration</span>
          <span className="text-right text-xs font-black text-slate-800">
            {formatDuration(log.duration_ms)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold text-slate-400">Time</span>
          <span className="text-right text-xs font-black text-slate-800">
            {formatDate(log.created_at)}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-bold text-slate-400">Status Group</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500">
            <span className={cn("h-2 w-2 rounded-full", statusMeta.dot)} />
            {statusMeta.label}
          </span>
        </div>
      </div>

      {open && (
        <div className="mt-4">
          <ExpandedLogDetails log={log} />
        </div>
      )}
    </div>
  );
}

export default function ApiLogsPage() {
  const [data, setData] = useState<ApiLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [requesterType, setRequesterType] = useState("");
  const [method, setMethod] = useState("");
  const [statusCode, setStatusCode] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const summary = data?.summary;
  const logs = data?.items || [];

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const filters = useMemo(
    () => ({
      page,
      page_size: pageSize,
      requester_type: requesterType,
      method,
      status_code: statusCode,
      search,
    }),
    [page, pageSize, requesterType, method, statusCode, search],
  );

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getApiLogs(filters);

      setData(response);
    } catch (error) {
      console.error("Failed to load API logs:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  function resetFilters() {
    setPage(1);
    setRequesterType("");
    setMethod("");
    setStatusCode("");
    setSearch("");
    setSearchInput("");
  }

  const hasActiveFilters =
    requesterType || method || statusCode || search || searchInput;

  const canGoPrev = page > 1;
  const canGoNext = data ? page < data.total_pages : false;

  const totalLogs = summary?.total_logs || 0;
  const successLogs = summary?.success_logs || 0;
  const failedLogs = summary?.failed_logs || 0;

  const successRate =
    totalLogs > 0 ? Math.round((successLogs / totalLogs) * 100) : 0;

  return (
    <div className="min-h-full w-full bg-[linear-gradient(180deg,#eef5ff_0%,#f8fbff_100%)] px-2 py-3 sm:px-3 md:px-4 md:py-4">
      <div className="w-full max-w-none space-y-4">
        <section className="relative overflow-hidden rounded-3xl bg-slate-950 p-5 text-white shadow-xl md:p-6">
          <div className="absolute right-[-80px] top-[-80px] h-64 w-64 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute bottom-[-100px] left-1/3 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />

          <div className="relative flex flex-col justify-between gap-6 xl:flex-row xl:items-center">
            <div className="max-w-3xl">
              {/* <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-bold text-white/80 backdrop-blur">
                <Sparkles size={14} />
                Super Admin Control Center
              </div> */}

              <div className="flex items-start gap-4">
                <div className="hidden rounded-3xl bg-white p-4 text-slate-950 shadow-xl sm:block">
                  <Activity size={28} />
                </div>

                <div>
                  <h1 className="text-3xl font-black tracking-tight md:text-4xl">
                    API Request Logs
                  </h1>

                  <p className="mt-2 max-w-2xl text-sm leading-6 text-white/65">
                    Monitor customer, technician and reseller activity with
                    response codes, devices, IP addresses, request timing and
                    authentication errors.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[460px]">
              <MiniMetric
                title="Success Rate"
                value={successRate}
                icon={<CheckCircle2 size={18} />}
              />

              <MiniMetric
                title="Failed Logs"
                value={failedLogs}
                icon={<AlertTriangle size={18} />}
              />

              <MiniMetric
                title="Current Page"
                value={data?.page || page}
                icon={<Clock size={18} />}
              />
            </div>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Total Logs"
            value={totalLogs}
            helper="All captured requests"
            icon={<Activity size={23} />}
            className="bg-blue-600"
          />

          <StatCard
            title="Successful"
            value={successLogs}
            helper="2xx and successful responses"
            icon={<CheckCircle2 size={23} />}
            className="bg-emerald-600"
          />

          <StatCard
            title="Failed"
            value={failedLogs}
            helper="4xx and 5xx responses"
            icon={<AlertTriangle size={23} />}
            className="bg-rose-600"
          />

          <StatCard
            title="Portal Users"
            value={
              (summary?.customer_logs || 0) +
              (summary?.technician_logs || 0) +
              (summary?.reseller_logs || 0)
            }
            helper="Customers, technicians, resellers"
            icon={<Users size={23} />}
            className="bg-violet-600"
          />
        </section>

        <section className="rounded-3xl border border-white bg-white/95 p-4 shadow-sm backdrop-blur">
          <div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center">
            <div>
              <div className="flex items-center gap-2">
                <div className="rounded-2xl bg-slate-950 p-2.5 text-white">
                  <Filter size={18} />
                </div>

                <div>
                  <h2 className="font-black text-slate-950">
                    Search & Filters
                  </h2>

                  <p className="text-xs font-medium text-slate-400">
                    Narrow logs by path, requester, method or response status.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50"
                >
                  Clear filters
                </button>
              )}

              <button
                onClick={loadLogs}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw
                  size={16}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-400">
                Search
              </label>

              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 transition focus-within:border-blue-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100">
                <Search size={17} className="text-slate-400" />

                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Path, name, phone, email, IP..."
                  className="w-full border-none bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
                />
              </div>
            </div>

            <div className="lg:col-span-3">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-400">
                Requester
              </label>

              <select
                value={requesterType}
                onChange={(event) => {
                  setPage(1);
                  setRequesterType(event.target.value);
                }}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
              >
                {requesterOptions.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-400">
                Method
              </label>

              <select
                value={method}
                onChange={(event) => {
                  setPage(1);
                  setMethod(event.target.value);
                }}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
              >
                {methodOptions.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-400">
                Status
              </label>

              <select
                value={statusCode}
                onChange={(event) => {
                  setPage(1);
                  setStatusCode(event.target.value);
                }}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100"
              >
                {statusOptions.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-3xl border border-white bg-white/95 shadow-sm backdrop-blur">
          <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-4 md:flex-row md:items-center md:p-5">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-blue-50 p-3 text-blue-700">
                <Shield size={20} />
              </div>

              <div>
                <h2 className="font-black text-slate-950">Request History</h2>

                <p className="text-xs font-medium text-slate-400">
                  {formatNumber(data?.total || 0)} result
                  {data?.total === 1 ? "" : "s"} found
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center sm:w-auto">
              <div className="rounded-2xl bg-blue-50 px-3 py-2">
                <p className="text-[10px] font-black uppercase text-blue-400">
                  Customers
                </p>

                <p className="text-sm font-black text-blue-900">
                  {formatNumber(summary?.customer_logs || 0)}
                </p>
              </div>

              <div className="rounded-2xl bg-violet-50 px-3 py-2">
                <p className="text-[10px] font-black uppercase text-violet-400">
                  Techs
                </p>

                <p className="text-sm font-black text-violet-900">
                  {formatNumber(summary?.technician_logs || 0)}
                </p>
              </div>

              <div className="rounded-2xl bg-amber-50 px-3 py-2">
                <p className="text-[10px] font-black uppercase text-amber-500">
                  Resellers
                </p>

                <p className="text-sm font-black text-amber-900">
                  {formatNumber(summary?.reseller_logs || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 lg:hidden">
            {loading && (
              <div className="flex items-center justify-center gap-2 rounded-3xl border border-slate-200 bg-slate-50 px-4 py-10 text-sm font-bold text-slate-500">
                <RefreshCw size={18} className="animate-spin" />
                Loading API logs...
              </div>
            )}

            {!loading && logs.length === 0 && (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center">
                <Server className="mx-auto text-slate-300" size={34} />

                <p className="mt-3 font-black text-slate-800">No logs found</p>

                <p className="mt-1 text-sm text-slate-400">
                  Try changing the search or filters.
                </p>
              </div>
            )}

            {!loading && logs.length > 0 && (
              <div className="space-y-3">
                {logs.map((log) => (
                  <MobileLogCard key={log.id} log={log} />
                ))}
              </div>
            )}
          </div>

          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full min-w-[1120px] border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-black uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-4">Method / Time</th>
                  <th className="px-5 py-4">Endpoint</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Requester</th>
                  <th className="px-5 py-4">IP / Device</th>
                  <th className="px-5 py-4">Duration</th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-5 py-14 text-center">
                      <div className="flex items-center justify-center gap-2 text-sm font-bold text-slate-500">
                        <RefreshCw size={18} className="animate-spin" />
                        Loading API logs...
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && logs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <Globe2 className="mx-auto text-slate-300" size={38} />

                      <p className="mt-3 font-black text-slate-800">
                        No logs found
                      </p>

                      <p className="mt-1 text-sm text-slate-400">
                        Try changing the filters or search value.
                      </p>
                    </td>
                  </tr>
                )}

                {!loading &&
                  logs.map((log) => <DesktopLogRow key={log.id} log={log} />)}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col justify-between gap-3 border-t border-slate-100 p-4 md:flex-row md:items-center md:p-5">
            <div className="text-sm font-bold text-slate-500">
              Page <span className="text-slate-950">{data?.page || page}</span>{" "}
              of{" "}
              <span className="text-slate-950">{data?.total_pages || 1}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={!canGoPrev || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowLeft size={16} />
                Previous
              </button>

              <button
                disabled={!canGoNext || loading}
                onClick={() => setPage((prev) => prev + 1)}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
